import { useState } from 'react';
import { motion } from 'framer-motion';
import { Box, Layers, Code, ArrowLeft, ArrowRight, Zap } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import Seo from '@/components/Seo';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import FaqSection, { faqs } from '@/components/sections/FaqSection';
import { serviceDetails } from '@/data/services';
import { Link } from 'react-router-dom';

import img3achs from '@/assets/gallery/gehaeuse-01.png';
import img5achs from '@/assets/gallery/5achs-bauteil-01.png';
import imgCam from '@/assets/machines/onecnc-cadcam.jpg';

const services = [
  {
    icon: Box,
    title: '3-Achs Fräsen',
    subtitle: 'Wirtschaftliche Bearbeitung von Einzelteilen und Kleinserien',
    image: img3achs,
    backContent: {
      description: 'Wirtschaftliche Bearbeitung von Einzelteilen und Kleinserien mit höchster Präzision.',
      items: ['Gehäuse', 'Platten', 'Vorrichtungsteile'],
      label: 'Typische Bauteile',
    },
  },
  {
    icon: Layers,
    title: '5-Achs Fräsen',
    subtitle: 'Effiziente Komplettbearbeitung mit reduzierten Spannvorgängen',
    image: img5achs,
    backContent: {
      description: 'Effiziente Komplettbearbeitung mit reduzierten Spannvorgängen für maximale Genauigkeit.',
      items: ['Mehrseitenbearbeitung', 'Schräge Flächen oder Bohrungen', 'Komplexere Konturen'],
      label: 'Vorteile',
    },
  },
  {
    icon: Code,
    title: 'CAM Programmierung',
    subtitle: 'OneCNC – Intelligente Werkzeugwege',
    image: imgCam,
    backContent: {
      description: 'Erstellen und Verwalten von intelligenten Werkzeugwegen mit OneCNC.',
      items: [],
      label: 'Formate',
      formats: [
        { label: 'Step', ext: '.step, .stp' },
        { label: 'IGES', ext: '.iges, .igs' },
        { label: 'SolidWorks', ext: '.sldprt' },
        { label: 'DXF', ext: '.dxf' },
        { label: 'DWG', ext: '.dwg' },
      ],
    },
  },
];

const FlipCard = ({ service, index }: { service: typeof services[0]; index: number }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const Icon = service.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.15 }}
      className="group w-full"
    >
      {/* Mobile only: stacked front/back, no flip */}
      <div className="md:hidden rounded-2xl overflow-hidden shadow-xl flex flex-col">
        {/* Front image */}
        <div className="relative aspect-[4/3]">
          <img loading="lazy" decoding="async" src={service.image} alt={service.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-5">
            <div className="w-10 h-10 rounded-xl bg-primary/90 flex items-center justify-center mb-3">
              <Icon className="w-5 h-5 text-primary-foreground" />
            </div>
            <h3 className="font-heading font-bold text-xl text-white mb-1">{service.title}</h3>
            <p className="text-white/80 text-sm">{service.subtitle}</p>
          </div>
        </div>
        {/* Back content always visible on mobile */}
        <div className="bg-primary text-primary-foreground p-5 flex flex-col gap-4">
          <p className="text-white/80 leading-relaxed text-sm">{service.backContent.description}</p>

          {service.backContent.items.length > 0 && (
            <div>
              <p className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-2">
                {service.backContent.label}
              </p>
              <ul className="space-y-1.5">
                {service.backContent.items.map((item) => (
                  <li key={item} className="flex items-center gap-2 text-white text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-white/60 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {service.backContent.formats && (
            <div>
              <p className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-2">
                {service.backContent.label}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {service.backContent.formats.map((f) => (
                  <Badge key={f.label} variant="secondary" className="bg-white/20 text-white border-white/30 text-xs">
                    {f.label} <span className="ml-1 text-white/60">({f.ext})</span>
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <Button variant="outline" size="sm" className="w-full border-white/30 text-white hover:bg-white/10 hover:text-white gap-2 mt-1" asChild>
            <a href="/anfrage">
              <Zap className="w-4 h-4" />
              Jetzt anfragen
            </a>
          </Button>
        </div>
      </div>

      {/* Tablet + Desktop: flip card */}
      <div
        className="hidden md:block perspective-1000 cursor-pointer"
        onClick={() => setIsFlipped(!isFlipped)}
        onMouseEnter={() => setIsFlipped(true)}
        onMouseLeave={() => setIsFlipped(false)}
      >
        <div
          className={`relative w-full aspect-[4/5] md:aspect-[3/4] lg:aspect-[3/4] transition-transform duration-700 preserve-3d ${
            isFlipped ? 'rotate-y-180' : ''
          }`}
        >
          {/* Front */}
          <div className="absolute inset-0 backface-hidden rounded-2xl overflow-hidden shadow-xl">
            <img loading="lazy" decoding="async" src={service.image} alt={service.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6 lg:p-8">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-primary/90 flex items-center justify-center mb-3 md:mb-4">
                <Icon className="w-5 h-5 md:w-6 md:h-6 text-primary-foreground" />
              </div>
              <h3 className="font-heading font-bold text-xl md:text-2xl lg:text-3xl text-white mb-1 md:mb-2">{service.title}</h3>
              <p className="text-white/80 text-sm">{service.subtitle}</p>
              <div className="mt-3 flex items-center gap-2 text-white/60 text-sm">
                <span>Details anzeigen</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </div>

          {/* Back */}
          <div className="absolute inset-0 backface-hidden rotate-y-180 rounded-2xl overflow-y-auto shadow-xl bg-primary text-primary-foreground p-5 md:p-6 lg:p-8 flex flex-col justify-start">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-white/20 flex items-center justify-center mb-3 md:mb-5">
              <Icon className="w-5 h-5 md:w-6 md:h-6 text-white" />
            </div>
            <h3 className="font-heading font-bold text-xl md:text-2xl lg:text-3xl mb-2 md:mb-3">{service.title}</h3>
            <p className="text-white/80 mb-3 md:mb-5 leading-relaxed text-sm md:text-base">{service.backContent.description}</p>

            {service.backContent.items.length > 0 && (
              <div className="mb-3 md:mb-5">
                <p className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-2">
                  {service.backContent.label}
                </p>
                <ul className="space-y-1.5 md:space-y-2">
                  {service.backContent.items.map((item) => (
                    <li key={item} className="flex items-center gap-2 text-white text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-white/60 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {service.backContent.formats && (
              <div className="mb-3 md:mb-5">
                <p className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-2">
                  {service.backContent.label}
                </p>
                <div className="flex flex-wrap gap-1.5 md:gap-2">
                  {service.backContent.formats.map((f) => (
                    <Badge key={f.label} variant="secondary" className="bg-white/20 text-white border-white/30 hover:bg-white/30 text-xs">
                      {f.label} <span className="ml-1 text-white/60">({f.ext})</span>
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div className="pt-3 md:pt-4">
              <Button variant="outline" size="sm" className="w-full border-white/30 text-white hover:bg-white/10 hover:text-white gap-2" asChild>
                <a href="/anfrage">
                  <Zap className="w-4 h-4" />
                  Jetzt anfragen
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const Leistungen = () => {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Seo
        title="CNC-Fräsen 3-Achs & 5-Achs, CAM-Programmierung | SR-Zerspanung"
        description="Unsere Leistungen: 3-Achs und 5-Achs CNC-Fräsen bis 610 x 420 mm, CAM-Programmierung mit OneCNC, Einzelteile und Kleinserien aus Stamsried in der Oberpfalz."
        path="/leistungen"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Startseite', item: 'https://www.sr-zerspanung.de/' },
              { '@type': 'ListItem', position: 2, name: 'Leistungen', item: 'https://www.sr-zerspanung.de/leistungen' },
            ],
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: { '@type': 'Answer', text: faq.answer },
            })),
          },
        ]}
      />
      <Header />
      <main className="flex-1 pt-28 md:pt-32 lg:pt-24">
        {/* Hero */}
        <section className="py-20 lg:py-32 bg-gradient-steel">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto">
              <motion.a
                href="/"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
                className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
              >
                <ArrowLeft className="w-4 h-4" />
                Zurück zur Startseite
              </motion.a>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-primary font-semibold uppercase tracking-widest text-sm mb-4"
              >
                CNC-Fertigung &amp; Programmierung
              </motion.p>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-foreground mb-6"
              >
                Unsere Leistungen –
                <span className="text-gradient-primary block mt-1">Was wir für Sie fertigen</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-muted-foreground text-lg lg:text-xl max-w-2xl mx-auto"
              >
                Vom Einzelteil bis zur Kleinserie – mit modernstem Maschinenpark und durchdachter CAM-Programmierung.
              </motion.p>
            </div>
          </div>
        </section>

        {/* Interactive Flip Cards */}
        <section className="py-16 lg:py-24">
          <div className="container mx-auto px-4">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center text-muted-foreground mb-12"
            >
              Karte anklicken oder hovern für Details
            </motion.p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {services.map((service, index) => (
                <FlipCard key={service.title} service={service} index={index} />
              ))}
            </div>
          </div>
        </section>

        {/* Detailseiten */}
        <section className="py-16 lg:py-24 bg-muted/40">
          <div className="container mx-auto px-4">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-foreground mb-4 text-center">
              Leistungen im Detail
            </h2>
            <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
              Technische Eckdaten, typische Bauteile und Antworten auf die häufigsten Fragen – je Verfahren auf einer eigenen Seite.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {serviceDetails.map((s) => (
                <Link
                  key={s.slug}
                  to={`/leistungen/${s.slug}`}
                  className="group rounded-xl border border-border bg-card p-6 h-full flex flex-col justify-between hover:border-primary transition-colors"
                >
                  <div>
                    <h3 className="font-heading font-bold text-lg text-foreground mb-2">{s.name}</h3>
                    <p className="text-muted-foreground text-sm">{s.metaDescription}</p>
                  </div>
                  <span className="mt-4 inline-flex items-center gap-2 text-primary text-sm font-semibold">
                    Mehr erfahren
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <FaqSection />
      </main>
      <Footer />
    </div>
  );
};

export default Leistungen;
